import { computed, onMounted, reactive, ref, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useMazdaWriteScope } from '@/composables/mazda/useMazdaWriteScope';

const VERSION_RE = /^V\d{2}\.\d{2}$/;
const SYSTEM_LIST_KEYS = new Set(['ParamList', 'SoList', 'Sn']);
const soDiagOptions = [
  { label: 'MAZDA', value: 'MAZDA' },
  { label: '防盗 IMM_MAZDA', value: 'IMM_MAZDA' },
];

function versionToNumber(version) {
  if (!VERSION_RE.test(String(version || ''))) return -1;
  return Number(version.slice(1, 3)) * 100 + Number(version.slice(4, 6));
}

function cloneRows(rows) {
  return Array.isArray(rows) ? rows.map((row) => ({ ...row })) : [];
}

function filterSystemRows(rows) {
  return cloneRows(rows).filter((row) => !SYSTEM_LIST_KEYS.has(String(row?.key || row?.sn || row?.value || '').trim()));
}

function makeTodaySoValue() {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `0x${yy}${mm}${dd}01`;
}

export function useMazdaSoVersion(mazdaPost, serverName) {
  const loading = ref(false);
  const saving = ref(false);
  const soDiagId = ref('MAZDA');
  const versions = ref([]);
  const versionList = ref([]);
  const currentVersion = ref('');
  const officialVersion = ref('');
  const testVersion = ref('');
  const editOfficialVersion = ref('');
  const editTestVersion = ref('');
  const prefix = ref('api:SoVersion:MAZDA');
  const defaultSoNames = ref([]);
  const defaultSoValue = ref(makeTodaySoValue());
  const { localOnly, scopedMazdaPost } = useMazdaWriteScope(mazdaPost, [
    'soVersionUpdateCurrent',
    'soVersionAddVersion',
    'soVersionSetParam',
    'soVersionDeleteParam',
    'soVersionSetSoItem',
    'soVersionDeleteSoItem',
    'soVersionAddSn',
    'soVersionDeleteSn',
  ]);

  const addVersionDialogVisible = ref(false);
  const addParamDialogVisible = ref(false);
  const addSoDialogVisible = ref(false);
  const addSnDialogVisible = ref(false);

  const newVersionForm = reactive({
    version: '',
    copyFromVersion: '',
    setAsTest: true,
  });
  const newParamForm = reactive({ key: '', value: 'FALSE' });
  const newSoForm = reactive({ key: '', value: makeTodaySoValue() });
  const newSnForm = reactive({ sn: '' });

  const currentVersionObj = computed(() => versions.value.find((item) => item.name === currentVersion.value) || null);
  const selectedSoDiagLabel = computed(() => soDiagOptions.find((item) => item.value === soDiagId.value)?.label || soDiagId.value);
  const currentParams = computed(() => filterSystemRows(currentVersionObj.value?.params || []));
  const currentSoItems = computed(() => filterSystemRows(currentVersionObj.value?.keyValuePairs || currentVersionObj.value?.soItems || []));
  const currentSnItems = computed(() => cloneRows(currentVersionObj.value?.snItems || []));
  const currentParamListKey = computed(() => currentVersionObj.value?.paramListKey || (currentVersion.value ? `${prefix.value}:${currentVersion.value}:ParamList` : ''));
  const currentSoListKey = computed(() => currentVersionObj.value?.soListKey || (currentVersion.value ? `${prefix.value}:${currentVersion.value}:SoList` : ''));
  const currentSnListKey = computed(() => currentVersionObj.value?.snListKey || (currentVersion.value ? `${prefix.value}:${currentVersion.value}:Sn` : ''));

  const resetForms = () => {
    newVersionForm.version = '';
    newVersionForm.copyFromVersion = currentVersion.value || testVersion.value || officialVersion.value || '';
    newVersionForm.setAsTest = true;
    newParamForm.key = '';
    newParamForm.value = 'FALSE';
    newSoForm.key = '';
    newSoForm.value = defaultSoValue.value || makeTodaySoValue();
    newSnForm.sn = '';
  };

  const applyInfo = (data) => {
    const list = Array.isArray(data.versions) ? data.versions : [];
    versions.value = list.map((item) => ({
      ...item,
      params: filterSystemRows(item.params),
      keyValuePairs: filterSystemRows(item.keyValuePairs || item.soItems),
      soItems: filterSystemRows(item.soItems || item.keyValuePairs),
      snItems: cloneRows(item.snItems),
    }));
    versionList.value = Array.isArray(data.versionList) ? data.versionList : versions.value.map((item) => item.name);
    officialVersion.value = data.officialVersion || '';
    testVersion.value = data.testVersion || '';
    editOfficialVersion.value = officialVersion.value;
    editTestVersion.value = testVersion.value;
    soDiagId.value = data.diagId || soDiagId.value || 'MAZDA';
    prefix.value = data.prefix || `api:SoVersion:${soDiagId.value}`;
    defaultSoNames.value = Array.isArray(data.defaultSoNames) ? data.defaultSoNames : [];
    defaultSoValue.value = data.defaultSoValue || makeTodaySoValue();

    if (!currentVersion.value || !versionList.value.includes(currentVersion.value)) {
      currentVersion.value = testVersion.value || officialVersion.value || versionList.value[0] || '';
    }

    resetForms();
  };

  const loadSoVersionInfo = async () => {
    loading.value = true;
    try {
      const response = await mazdaPost('soVersionInfo', {
        diagId: soDiagId.value,
      });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '读取统一项失败');
        return;
      }
      applyInfo(data);
    } catch (error) {
      ElMessage.error(`读取统一项失败: ${error.response?.data?.message || error.message}`);
    } finally {
      loading.value = false;
    }
  };

  const validateCurrentVersions = () => {
    const official = String(editOfficialVersion.value || '').trim();
    const test = String(editTestVersion.value || '').trim();
    if (!VERSION_RE.test(official)) {
      throw new Error('正式版本格式必须是 Vxx.xx，例如 V44.40');
    }
    if (!VERSION_RE.test(test)) {
      throw new Error('测试版本格式必须是 Vxx.xx，例如 V44.40');
    }
    if (versionToNumber(test) < versionToNumber(official)) {
      throw new Error('测试版本必须大于或等于正式版本');
    }
    return { official, test };
  };

  const saveCurrentVersions = async () => {
    let payload;
    try {
      const { official, test } = validateCurrentVersions();
      payload = { diagId: soDiagId.value, officialVersion: official, testVersion: test };
    } catch (error) {
      ElMessage.error(error.message);
      return;
    }

    try {
      await ElMessageBox.confirm(
        `确定修改当前版本吗？\n\n正式版本：${payload.officialVersion}\n测试版本：${payload.testVersion}`,
        '确认修改版本',
        { confirmButtonText: '确定修改', cancelButtonText: '取消', type: 'warning' },
      );
    } catch {
      return;
    }

    saving.value = true;
    try {
      const response = await scopedMazdaPost('soVersionUpdateCurrent', payload);
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '保存当前版本失败');
        return;
      }
      applyInfo(data);
      ElMessage.success(data.message || '保存当前版本成功');
    } catch (error) {
      ElMessage.error(`保存当前版本失败: ${error.response?.data?.message || error.message}`);
    } finally {
      saving.value = false;
    }
  };

  const openAddVersionDialog = () => {
    resetForms();
    addVersionDialogVisible.value = true;
  };

  const submitAddVersion = async () => {
    const version = String(newVersionForm.version || '').trim();
    const copyFromVersion = String(newVersionForm.copyFromVersion || '').trim();
    if (!VERSION_RE.test(version)) {
      ElMessage.error('新增版本格式必须是 Vxx.xx，例如 V44.40');
      return;
    }
    if (newVersionForm.setAsTest && editOfficialVersion.value && VERSION_RE.test(editOfficialVersion.value)) {
      if (versionToNumber(version) < versionToNumber(editOfficialVersion.value)) {
        ElMessage.error('新增测试版本不能小于当前正式版本');
        return;
      }
    }

    saving.value = true;
    try {
      const response = await scopedMazdaPost('soVersionAddVersion', {
        diagId: soDiagId.value,
        version,
        copyFromVersion,
        setAsTest: !!newVersionForm.setAsTest,
      });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '新增版本失败');
        return;
      }
      applyInfo(data);
      currentVersion.value = version;
      addVersionDialogVisible.value = false;
      ElMessage.success(data.message || '新增版本成功');
    } catch (error) {
      ElMessage.error(`新增版本失败: ${error.response?.data?.message || error.message}`);
    } finally {
      saving.value = false;
    }
  };

  const openAddParamDialog = () => {
    newParamForm.key = '';
    newParamForm.value = 'FALSE';
    addParamDialogVisible.value = true;
  };

  const saveParam = async (row) => {
    if (!currentVersion.value) {
      ElMessage.warning('请先选择版本');
      return false;
    }
    const key = String(row?.key || '').trim();
    let value = String(row?.value ?? '').trim();
    if (!key) {
      ElMessage.error('参数名称不能为空');
      return false;
    }
    if (SYSTEM_LIST_KEYS.has(key)) {
      ElMessage.error('ParamList / SoList / Sn 是系统字段，不能作为参数保存');
      return false;
    }
    value = value.toUpperCase();
    if (!['TRUE', 'FALSE'].includes(value)) {
      ElMessage.error('参数值只能选择 TRUE 或 FALSE');
      return false;
    }

    saving.value = true;
    try {
      const response = await scopedMazdaPost('soVersionSetParam', { diagId: soDiagId.value, version: currentVersion.value, key, value });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '保存参数失败');
        return false;
      }
      applyInfo(data);
      ElMessage.success(data.message || '保存参数成功');
      return true;
    } catch (error) {
      ElMessage.error(`保存参数失败: ${error.response?.data?.message || error.message}`);
      return false;
    } finally {
      saving.value = false;
    }
  };

  const submitAddParam = async () => {
    const ok = await saveParam({ key: newParamForm.key, value: newParamForm.value });
    if (ok) {
      addParamDialogVisible.value = false;
    }
  };

  const deleteParam = async (row) => {
    if (!currentVersion.value || !row?.key) return;
    try {
      await ElMessageBox.confirm(
        `确定删除参数吗？\n\n版本：${currentVersion.value}\n参数：${row.key}`,
        '确认删除',
        { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' },
      );
    } catch {
      return;
    }

    saving.value = true;
    try {
      const response = await scopedMazdaPost('soVersionDeleteParam', { diagId: soDiagId.value, version: currentVersion.value, key: row.key });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '删除参数失败');
        return;
      }
      applyInfo(data);
      ElMessage.success(data.message || '删除参数成功');
    } catch (error) {
      ElMessage.error(`删除参数失败: ${error.response?.data?.message || error.message}`);
    } finally {
      saving.value = false;
    }
  };

  const openAddSoDialog = () => {
    newSoForm.key = '';
    newSoForm.value = defaultSoValue.value || makeTodaySoValue();
    addSoDialogVisible.value = true;
  };

  const saveSoItem = async (row) => {
    if (!currentVersion.value) {
      ElMessage.warning('请先选择版本');
      return false;
    }
    const key = String(row?.key || '').trim();
    const value = String(row?.value ?? '').trim();
    if (!key || !value) {
      ElMessage.error('SO 名称和版本都不能为空');
      return false;
    }
    if (SYSTEM_LIST_KEYS.has(key)) {
      ElMessage.error('ParamList / SoList / Sn 是系统字段，不能作为 SO 保存');
      return false;
    }

    saving.value = true;
    try {
      const response = await scopedMazdaPost('soVersionSetSoItem', { diagId: soDiagId.value, version: currentVersion.value, key, value });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '保存 SO 版本失败');
        return false;
      }
      applyInfo(data);
      ElMessage.success(data.message || '保存 SO 版本成功');
      return true;
    } catch (error) {
      ElMessage.error(`保存 SO 版本失败: ${error.response?.data?.message || error.message}`);
      return false;
    } finally {
      saving.value = false;
    }
  };

  const submitAddSo = async () => {
    const ok = await saveSoItem({ key: newSoForm.key, value: newSoForm.value });
    if (ok) {
      addSoDialogVisible.value = false;
    }
  };

  const deleteSoItem = async (row) => {
    if (!currentVersion.value || !row?.key) return;
    try {
      await ElMessageBox.confirm(
        `确定删除 SO 版本项吗？\n\n版本：${currentVersion.value}\nSO：${row.key}`,
        '确认删除',
        { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' },
      );
    } catch {
      return;
    }

    saving.value = true;
    try {
      const response = await scopedMazdaPost('soVersionDeleteSoItem', { diagId: soDiagId.value, version: currentVersion.value, key: row.key });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '删除 SO 版本失败');
        return;
      }
      applyInfo(data);
      ElMessage.success(data.message || '删除 SO 版本成功');
    } catch (error) {
      ElMessage.error(`删除 SO 版本失败: ${error.response?.data?.message || error.message}`);
    } finally {
      saving.value = false;
    }
  };

  const openAddSnDialog = () => {
    newSnForm.sn = '';
    addSnDialogVisible.value = true;
  };

  const submitAddSn = async () => {
    if (!currentVersion.value) {
      ElMessage.warning('请先选择版本');
      return;
    }
    const sn = String(newSnForm.sn || '').trim();
    if (!sn) {
      ElMessage.error('SN 不能为空');
      return;
    }

    saving.value = true;
    try {
      const response = await scopedMazdaPost('soVersionAddSn', { diagId: soDiagId.value, version: currentVersion.value, sn });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '添加 SN 失败');
        return;
      }
      applyInfo(data);
      addSnDialogVisible.value = false;
      ElMessage.success(data.message || '添加 SN 成功');
    } catch (error) {
      ElMessage.error(`添加 SN 失败: ${error.response?.data?.message || error.message}`);
    } finally {
      saving.value = false;
    }
  };

  const deleteSn = async (row) => {
    const sn = String(row?.sn || row?.value || '').trim();
    if (!currentVersion.value || !sn) return;
    try {
      await ElMessageBox.confirm(
        `确定删除 SN 吗？\n\n版本：${currentVersion.value}\nSN：${sn}`,
        '确认删除',
        { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' },
      );
    } catch {
      return;
    }

    saving.value = true;
    try {
      const response = await scopedMazdaPost('soVersionDeleteSn', { diagId: soDiagId.value, version: currentVersion.value, sn });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '删除 SN 失败');
        return;
      }
      applyInfo(data);
      ElMessage.success(data.message || '删除 SN 成功');
    } catch (error) {
      ElMessage.error(`删除 SN 失败: ${error.response?.data?.message || error.message}`);
    } finally {
      saving.value = false;
    }
  };

  watch(serverName, loadSoVersionInfo);
  watch(soDiagId, () => {
    currentVersion.value = '';
    loadSoVersionInfo();
  });
  onMounted(loadSoVersionInfo);

  return {
    localOnly,
    loading,
    saving,
    soDiagId,
    soDiagOptions,
    selectedSoDiagLabel,
    versions,
    versionList,
    currentVersion,
    officialVersion,
    testVersion,
    editOfficialVersion,
    editTestVersion,
    prefix,
    defaultSoNames,
    defaultSoValue,
    addVersionDialogVisible,
    addParamDialogVisible,
    addSoDialogVisible,
    addSnDialogVisible,
    newVersionForm,
    newParamForm,
    newSoForm,
    newSnForm,
    currentVersionObj,
    currentParams,
    currentSoItems,
    currentSnItems,
    currentParamListKey,
    currentSoListKey,
    currentSnListKey,
    loadSoVersionInfo,
    saveCurrentVersions,
    openAddVersionDialog,
    submitAddVersion,
    openAddParamDialog,
    saveParam,
    submitAddParam,
    deleteParam,
    openAddSoDialog,
    saveSoItem,
    submitAddSo,
    deleteSoItem,
    openAddSnDialog,
    submitAddSn,
    deleteSn,
  };
}
